import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog';
import {
  Rocket,
  RefreshCw,
  ArrowLeft,
  Server,
  Globe,
  GitCommit,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RotateCcw,
  Eye,
  EyeOff,
  Copy,
  Check,
  Ban,
  Terminal,
  Activity,
  Layers,
  Sparkles,
  ExternalLink,
  ShieldCheck,
  Cpu,
  Info,
  Radio,
  Zap,
  Download,
  Trash2,
  Play,
  Pause,
  ArrowDownCircle,
  FileText,
  Search,
  Filter,
  Code2,
  Database,
  Send,
  SlidersHorizontal
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/v2`;

export default function DevModeDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { activeThemeObj } = useTheme();

  // Top Section Switcher: 'deployments' | 'logs'
  const [mainSection, setMainSection] = useState('deployments');

  // Active Service State for Deployments: 'web_server' | 'static_site'
  const [activeService, setActiveService] = useState('web_server');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deploys, setDeploys] = useState([]);
  const [isRenderConfigured, setIsRenderConfigured] = useState(false);
  const [servicesConfig, setServicesConfig] = useState({});
  const [serviceId, setServiceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [showServiceId, setShowServiceId] = useState(false);
  const [systemOverview, setSystemOverview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  // Trigger Modal state
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [triggerTargetService, setTriggerTargetService] = useState('web_server'); // 'web_server' | 'static_site' | 'both'
  const [commitInput, setCommitInput] = useState('');
  const [clearCacheOption, setClearCacheOption] = useState(false);
  const [triggeringDeploy, setTriggeringDeploy] = useState(false);

  // Retrieve Modal state
  const [selectedDeploy, setSelectedDeploy] = useState(null);
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [fetchingDetail, setFetchingDetail] = useState(false);

  // Rollback Modal state
  const [rollbackDeployTarget, setRollbackDeployTarget] = useState(null);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  // Cancel state
  const [cancelingDeployId, setCancelingDeployId] = useState(null);

  // Track previous live deploy to detect completion
  const previousLiveDeployIdRef = useRef(null);

  // ==========================================
  // LOGS SECTION STATE
  // ==========================================
  const [activeLogTab, setActiveLogTab] = useState('server'); // 'server' | 'internal' | 'external'
  const [serverLogs, setServerLogs] = useState([]);
  const [internalApiLogs, setInternalApiLogs] = useState([]);
  const [externalApiLogs, setExternalApiLogs] = useState([]);
  const [externalTokenSummary, setExternalTokenSummary] = useState(null);
  const [logLevelFilter, setLogLevelFilter] = useState('ALL');
  const [logMethodFilter, setLogMethodFilter] = useState('ALL');
  const [logStatusFilter, setLogStatusFilter] = useState('all');
  const [logServiceFilter, setLogServiceFilter] = useState('all');
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [isStreamingLogs, setIsStreamingLogs] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [showLogDetailModal, setShowLogDetailModal] = useState(false);
  const [clearingLogs, setClearingLogs] = useState(false);

  const terminalBottomRef = useRef(null);

  // Auto-scroll terminal when new logs arrive
  useEffect(() => {
    if (autoScroll && activeLogTab === 'server' && terminalBottomRef.current) {
      terminalBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [serverLogs, autoScroll, activeLogTab]);

  // Fetch deploys list and system overview
  const fetchDeploys = useCallback(async (serviceType = activeService, isManualRefresh = false) => {
    try {
      if (isManualRefresh) setRefreshing(true);
      const [deploysRes, overviewRes] = await Promise.allSettled([
        axios.get(`${API}/dev/deploys/`, { params: { service_type: serviceType, limit: 50, _t: Date.now() } }),
        axios.get(`${API}/dev/overview/`, { params: { _t: Date.now() } })
      ]);

      if (deploysRes.status === 'fulfilled') {
        const data = deploysRes.value.data;
        const newDeploysList = data.deploys || [];
        
        // Detect newly live deployment
        const currentLive = newDeploysList.find((d) => d.status === 'live');
        if (
          currentLive &&
          previousLiveDeployIdRef.current &&
          previousLiveDeployIdRef.current !== currentLive.id
        ) {
          const shortCommit = currentLive.commit?.id && currentLive.commit.id !== 'HEAD'
            ? currentLive.commit.id.substring(0, 7)
            : (currentLive.id ? currentLive.id.substring(0, 10) : 'latest');

          toast.success(`🚀 Deployment ${shortCommit} is now LIVE on ${serviceType === 'static_site' ? 'Static Site' : 'Web Server'}!`);
        }
        if (currentLive) {
          previousLiveDeployIdRef.current = currentLive.id;
        }

        setDeploys(newDeploysList);
        setIsRenderConfigured(Boolean(data.configured));
        setServiceId(data.service_id || '');
        setServiceName(data.service_name || '');
        if (data.services) {
          setServicesConfig(data.services);
        }
      }

      if (overviewRes.status === 'fulfilled') {
        setSystemOverview(overviewRes.value.data);
      }

      if (isManualRefresh) {
        toast.success(`Refreshed ${serviceType === 'static_site' ? 'Static Site' : 'Web Server'} deploys`);
      }
    } catch (err) {
      console.error('Error fetching deployments:', err);
      if (isManualRefresh) {
        toast.error('Failed to fetch deployments');
      }
    } finally {
      setLoading(false);
      if (isManualRefresh) setRefreshing(false);
    }
  }, [activeService]);

  // Fetch Logs based on active tab
  const fetchLogs = useCallback(async (tab = activeLogTab, showToast = false) => {
    try {
      if (tab === 'server') {
        const res = await axios.get(`${API}/dev/logs/server/`, {
          params: {
            level: logLevelFilter,
            search: logSearchQuery || undefined,
            limit: 300,
            _t: Date.now()
          }
        });
        setServerLogs(res.data?.logs || []);
      } else if (tab === 'internal') {
        const res = await axios.get(`${API}/dev/logs/internal-api/`, {
          params: {
            method: logMethodFilter,
            status_type: logStatusFilter,
            search: logSearchQuery || undefined,
            limit: 200,
            _t: Date.now()
          }
        });
        setInternalApiLogs(res.data?.logs || []);
      } else if (tab === 'external') {
        const res = await axios.get(`${API}/dev/logs/external-api/`, {
          params: {
            service: logServiceFilter,
            status_type: logStatusFilter,
            search: logSearchQuery || undefined,
            limit: 200,
            _t: Date.now()
          }
        });
        setExternalApiLogs(res.data?.logs || []);
        setExternalTokenSummary(res.data?.token_summary || null);
      }

      if (showToast) {
        toast.success('Logs refreshed');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      if (showToast) toast.error('Failed to fetch logs');
    }
  }, [activeLogTab, logLevelFilter, logMethodFilter, logStatusFilter, logServiceFilter, logSearchQuery]);

  // Re-fetch deployments when switching service tabs
  const handleServiceTabChange = (serviceType) => {
    setActiveService(serviceType);
    setTriggerTargetService(serviceType);
    fetchDeploys(serviceType);
  };

  // Deployment Polling Effect
  useEffect(() => {
    fetchDeploys(activeService);

    const hasActiveBuilding = deploys.some(
      (d) => d.status === 'build_in_progress' || d.status === 'queued' || d.status === 'in_progress'
    );
    const intervalMs = hasActiveBuilding ? 3500 : 10000;

    const interval = setInterval(() => {
      if (mainSection === 'deployments') {
        fetchDeploys(activeService, false);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [fetchDeploys, activeService, deploys, mainSection]);

  // Logs Background Streaming Effect
  useEffect(() => {
    if (mainSection === 'logs') {
      fetchLogs(activeLogTab);

      if (isStreamingLogs) {
        const logInterval = setInterval(() => {
          fetchLogs(activeLogTab, false);
        }, 2500);
        return () => clearInterval(logInterval);
      }
    }
  }, [mainSection, activeLogTab, fetchLogs, isStreamingLogs]);

  // Copy helper
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Mask Service ID with Asterisks
  const getMaskedServiceId = (id) => {
    if (!id || id === 'Local Demo Service') return id;
    if (showServiceId) return id;
    if (id.startsWith('srv-')) {
      return `srv-${'*'.repeat(Math.min(Math.max(id.length - 4, 12), 16))}`;
    }
    return '*'.repeat(Math.min(Math.max(id.length, 16), 20));
  };

  // Open trigger modal with specific target
  const handleOpenTriggerModal = (target = activeService) => {
    setTriggerTargetService(target);
    setCommitInput('');
    setClearCacheOption(false);
    setShowTriggerModal(true);
  };

  // Trigger deploy (single or both)
  const handleTriggerDeploy = async () => {
    try {
      setTriggeringDeploy(true);
      const payload = {
        service_type: triggerTargetService,
        clear_cache: clearCacheOption ? 'clear' : 'do_not_clear',
        commit_id: commitInput.trim() || undefined
      };
      const res = await axios.post(`${API}/dev/deploys/`, payload);
      toast.success(res.data?.message || 'Deploy triggered successfully!');
      setShowTriggerModal(false);
      setClearCacheOption(false);
      setCommitInput('');

      // Optimistic update if returned deploy exists
      if (res.data?.deploy && (triggerTargetService === activeService || triggerTargetService === 'both')) {
        setDeploys((prev) => [res.data.deploy, ...prev.filter((p) => p.id !== res.data.deploy.id)]);
      }

      // Schedule fast immediate syncs with Render API
      setTimeout(() => fetchDeploys(activeService), 1000);
      setTimeout(() => fetchDeploys(activeService), 3500);
      setTimeout(() => fetchDeploys(activeService), 7000);
    } catch (err) {
      console.error('Trigger deploy failed:', err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to trigger deploy');
    } finally {
      setTriggeringDeploy(false);
    }
  };

  // Retrieve single deploy details
  const handleInspectDeploy = async (deployId) => {
    try {
      setFetchingDetail(true);
      setShowRetrieveModal(true);
      const res = await axios.get(`${API}/dev/deploys/${deployId}/`, {
        params: { service_type: activeService }
      });
      setSelectedDeploy(res.data?.deploy || null);
    } catch (err) {
      console.error('Retrieve deploy failed:', err);
      toast.error('Failed to retrieve deploy details');
    } finally {
      setFetchingDetail(false);
    }
  };

  // Cancel deploy
  const handleCancelDeploy = async (deployId) => {
    try {
      setCancelingDeployId(deployId);
      const res = await axios.post(`${API}/dev/deploys/${deployId}/cancel/`, {
        service_type: activeService
      });
      toast.success(res.data?.message || `Deploy ${deployId} canceled.`);
      await fetchDeploys(activeService);
    } catch (err) {
      console.error('Cancel deploy failed:', err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to cancel deploy');
    } finally {
      setCancelingDeployId(null);
    }
  };

  // Rollback deploy
  const handleConfirmRollback = async () => {
    if (!rollbackDeployTarget) return;
    try {
      setRollingBack(true);
      const res = await axios.post(`${API}/dev/deploys/${rollbackDeployTarget.id}/rollback/`, {
        service_type: activeService
      });
      toast.success(res.data?.message || `Rollback to ${rollbackDeployTarget.id} initiated.`);
      setShowRollbackDialog(false);
      setRollbackDeployTarget(null);
      await fetchDeploys(activeService);
    } catch (err) {
      console.error('Rollback failed:', err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to rollback deploy');
    } finally {
      setRollingBack(false);
    }
  };

  // Clear logs handler
  const handleClearLogs = async () => {
    try {
      setClearingLogs(true);
      const endpointMap = {
        server: `${API}/dev/logs/server/`,
        internal: `${API}/dev/logs/internal-api/`,
        external: `${API}/dev/logs/external-api/`
      };
      await axios.post(endpointMap[activeLogTab], { action: 'clear' });
      toast.success(`Cleared ${activeLogTab} logs.`);
      fetchLogs(activeLogTab);
    } catch (err) {
      console.error('Clear logs failed:', err);
      toast.error('Failed to clear logs');
    } finally {
      setClearingLogs(false);
    }
  };

  // Export logs handler
  const handleExportLogs = (format = 'log') => {
    const url = `${API}/dev/logs/export/?type=${activeLogTab}&format=${format}`;
    window.open(url, '_blank');
    toast.success(`Exporting ${activeLogTab} logs (${format.toUpperCase()})`);
  };

  // Format status badge
  const getStatusBadge = (statusStr) => {
    switch (statusStr) {
      case 'live':
        return (
          <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 whitespace-nowrap'>
            <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse' />
            LIVE
          </span>
        );
      case 'build_in_progress':
      case 'in_progress':
      case 'queued':
        return (
          <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30 whitespace-nowrap'>
            <RefreshCw className='w-3 h-3 animate-spin text-blue-500' />
            BUILDING
          </span>
        );
      case 'build_failed':
      case 'failed':
        return (
          <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30 whitespace-nowrap'>
            <XCircle className='w-3 h-3 text-rose-500' />
            FAILED
          </span>
        );
      case 'canceled':
        return (
          <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-500/15 text-slate-600 dark:text-slate-400 border border-slate-500/30 whitespace-nowrap'>
            <Ban className='w-3 h-3 text-slate-500' />
            CANCELED
          </span>
        );
      case 'deactivated':
      default:
        return (
          <span className='inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border border-zinc-500/20 whitespace-nowrap'>
            <CheckCircle2 className='w-3 h-3 text-zinc-400' />
            DEACTIVATED
          </span>
        );
    }
  };

  // Helper date formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Duration calculation
  const getDuration = (startStr, endStr) => {
    if (!startStr || !endStr) return '—';
    try {
      const diffMs = new Date(endStr) - new Date(startStr);
      if (diffMs <= 0) return '—';
      const sec = Math.floor(diffMs / 1000);
      if (sec < 60) return `${sec}s`;
      const min = Math.floor(sec / 60);
      const remSec = sec % 60;
      return `${min}m ${remSec}s`;
    } catch {
      return '—';
    }
  };

  // HTTP Method color badges
  const getMethodBadge = (method = 'GET') => {
    const m = method.toUpperCase();
    if (m === 'GET') return <Badge className='bg-blue-500/15 text-blue-500 border-blue-500/30 font-mono text-[10px]'>GET</Badge>;
    if (m === 'POST') return <Badge className='bg-emerald-500/15 text-emerald-500 border-emerald-500/30 font-mono text-[10px]'>POST</Badge>;
    if (m === 'PUT' || m === 'PATCH') return <Badge className='bg-amber-500/15 text-amber-500 border-amber-500/30 font-mono text-[10px]'>{m}</Badge>;
    if (m === 'DELETE') return <Badge className='bg-rose-500/15 text-rose-500 border-rose-500/30 font-mono text-[10px]'>DEL</Badge>;
    return <Badge variant='outline' className='font-mono text-[10px]'>{m}</Badge>;
  };

  // HTTP Status code color pill
  const getStatusCodeBadge = (statusCode) => {
    if (!statusCode) return <Badge variant='outline' className='text-[10px] font-mono'>ERR</Badge>;
    if (statusCode >= 200 && statusCode < 300) {
      return <Badge className='bg-emerald-500/15 text-emerald-500 border-emerald-500/30 font-mono text-[10px]'>{statusCode}</Badge>;
    }
    if (statusCode >= 300 && statusCode < 400) {
      return <Badge className='bg-cyan-500/15 text-cyan-500 border-cyan-500/30 font-mono text-[10px]'>{statusCode}</Badge>;
    }
    if (statusCode >= 400 && statusCode < 500) {
      return <Badge className='bg-amber-500/15 text-amber-500 border-amber-500/30 font-mono text-[10px]'>{statusCode}</Badge>;
    }
    return <Badge className='bg-rose-500/15 text-rose-500 border-rose-500/30 font-mono text-[10px]'>{statusCode}</Badge>;
  };

  // Filter deploys
  const filteredDeploys = deploys.filter((d) => {
    const matchesSearch =
      (d.id && d.id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (d.commit?.message && d.commit.message.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (d.commit?.id && d.commit.id.toLowerCase().includes(searchQuery.toLowerCase()));

    if (!matchesSearch) return false;
    if (statusFilter === 'all') return true;
    if (statusFilter === 'live') return d.status === 'live';
    if (statusFilter === 'building') return d.status === 'build_in_progress' || d.status === 'queued';
    if (statusFilter === 'failed') return d.status === 'build_failed' || d.status === 'failed';
    if (statusFilter === 'canceled') return d.status === 'canceled';
    return true;
  });

  const liveDeploy = deploys.find((d) => d.status === 'live') || deploys[0];

  return (
    <div className={activeThemeObj.styles?.appBg || 'min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100'}>
      {/* Top Navigation Bar */}
      <header className={`border-b sticky top-0 z-50 transition-colors duration-300 ${activeThemeObj.styles?.headerBg || 'bg-slate-900/90 border-slate-800 backdrop-blur-md'}`}>
        <div className='container mx-auto px-4 py-3 flex items-center justify-between gap-3 max-w-7xl'>
          <div className='flex items-center gap-3 min-w-0'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => navigate('/workspace')}
              className='gap-2 text-xs sm:text-sm h-8 sm:h-9 hover:bg-accent/80'
            >
              <ArrowLeft className='h-4 w-4' />
              <span>Back to Workspace</span>
            </Button>
            <div className='h-4 w-px bg-border/60 hidden sm:block' />
            <div className='flex items-center gap-2 min-w-0'>
              <div className='h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md flex-shrink-0'>
                <Terminal className='h-4 w-4 text-white' />
              </div>
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <h1 className='text-sm sm:text-base font-bold truncate'>Dev Console</h1>
                  <Badge
                    variant='outline'
                    className='px-2 py-0.5 text-[10px] font-mono font-bold tracking-wider border border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center gap-1.5 rounded-md shadow-xs'
                  >
                    <span className='w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 animate-pulse' />
                    DEV MODE
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                if (mainSection === 'deployments') {
                  fetchDeploys(activeService, true);
                } else {
                  fetchLogs(activeLogTab, true);
                }
              }}
              disabled={refreshing}
              className='h-8 sm:h-9 text-xs gap-1.5'
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span className='hidden sm:inline'>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </Button>

            <div className='hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border bg-muted/30 border-border/50'>
              <ShieldCheck className='h-3.5 w-3.5 text-emerald-500' />
              <span className='truncate max-w-[120px]'>{user?.full_name || 'Staff User'}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Dashboard Container */}
      <main className='container mx-auto p-4 sm:p-6 max-w-7xl space-y-6'>
        {/* Render Connection Status Banner */}
        {!isRenderConfigured && (
          <div className='p-3.5 sm:p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-start gap-3 text-xs sm:text-sm text-amber-800 dark:text-amber-300'>
            <Info className='h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5' />
            <div className='space-y-1'>
              <p className='font-semibold'>Connected in Local Demo Mode</p>
              <p className='opacity-90 leading-relaxed text-xs'>
                Render API credentials (<code>RENDER_AUTH_KEY</code>, <code>WEB_SERVER_SERVICE_ID</code>, <code>STATIC_SITE_SERVICE_ID</code>) are not configured in the backend environment. Actions below operate safely on local simulated deployments.
              </p>
            </div>
          </div>
        )}

        {/* Top Overview Metric Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          {/* Card 1: Active Live Build */}
          <Card className={`p-4 border transition-colors ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
            <div className='flex items-center justify-between text-muted-foreground mb-2'>
              <span className='text-xs font-medium'>Live ({activeService === 'web_server' ? 'Backend' : 'Frontend'})</span>
              <Activity className='h-4 w-4 text-emerald-500' />
            </div>
            <div className='flex items-center justify-between'>
              <div className='min-w-0'>
                <div className='font-mono font-bold text-sm sm:text-base truncate'>
                  {liveDeploy?.id || 'No deploys'}
                </div>
                <div className='text-[11px] text-muted-foreground truncate mt-0.5'>
                  {liveDeploy?.commit?.message || 'Initial build'}
                </div>
              </div>
              <div className='flex-shrink-0 ml-2'>
                {liveDeploy ? getStatusBadge(liveDeploy.status) : <Badge variant='outline'>N/A</Badge>}
              </div>
            </div>
          </Card>

          {/* Card 2: Total Deploys Count */}
          <Card className={`p-4 border transition-colors ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
            <div className='flex items-center justify-between text-muted-foreground mb-2'>
              <span className='text-xs font-medium'>Deploys Recorded</span>
              <Layers className='h-4 w-4 text-blue-500' />
            </div>
            <div className='flex items-baseline gap-2'>
              <span className='text-2xl font-bold'>{deploys.length}</span>
              <span className='text-xs text-muted-foreground'>for this service</span>
            </div>
            <div className='text-[11px] text-muted-foreground mt-1 truncate'>
              Last updated: {liveDeploy?.finishedAt ? formatDate(liveDeploy.finishedAt) : 'Just now'}
            </div>
          </Card>

          {/* Card 3: Render Services Status with Animated Eye Toggle */}
          <Card className={`p-4 border transition-colors ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
            <div className='flex items-center justify-between text-muted-foreground mb-2'>
              <span className='text-xs font-medium'>Service Configured</span>
              <Server className='h-4 w-4 text-purple-500' />
            </div>
            <div className='flex items-center justify-between gap-1.5'>
              <div className='flex items-center gap-1.5 min-w-0'>
                <span className='w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0' />
                <span
                  className='font-mono text-xs font-semibold truncate transition-all duration-300'
                  title={showServiceId ? serviceId : 'Hidden Service ID'}
                >
                  {getMaskedServiceId(serviceId || 'Local Demo Service')}
                </span>
              </div>
              {serviceId && (
                <div className='flex items-center gap-1 flex-shrink-0'>
                  <button
                    type='button'
                    onClick={() => setShowServiceId((prev) => !prev)}
                    className='p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-300 ease-in-out transform active:scale-90 hover:scale-110 focus:outline-none'
                    title={showServiceId ? 'Hide Service ID' : 'Reveal Service ID'}
                  >
                    <div className='relative w-3.5 h-3.5 flex items-center justify-center transition-transform duration-300'>
                      {showServiceId ? (
                        <EyeOff className='h-3.5 w-3.5 text-purple-400 transition-all duration-300 transform rotate-0 scale-100' />
                      ) : (
                        <Eye className='h-3.5 w-3.5 text-muted-foreground transition-all duration-300 transform -rotate-12 scale-95 hover:rotate-0 hover:scale-100' />
                      )}
                    </div>
                  </button>
                  <button
                    type='button'
                    onClick={() => handleCopy(serviceId, 'service-id')}
                    className='p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-300 ease-in-out transform active:scale-90 hover:scale-110'
                    title='Copy Service ID'
                  >
                    {copiedId === 'service-id' ? (
                      <Check className='h-3.5 w-3.5 text-emerald-500' />
                    ) : (
                      <Copy className='h-3.5 w-3.5' />
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className='text-[11px] text-muted-foreground mt-1 flex items-center justify-between'>
              <span>Auth Key: {systemOverview?.render?.has_auth_key ? '✓ Configured' : 'Local'}</span>
            </div>
          </Card>

          {/* Card 4: Quick Action Trigger */}
          <Card className={`p-4 border transition-colors flex flex-col justify-between ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
            <div className='flex items-center justify-between text-muted-foreground mb-2'>
              <span className='text-xs font-medium'>Deploy Action</span>
              <Rocket className='h-4 w-4 text-indigo-400' />
            </div>
            <div className='grid grid-cols-2 gap-2 mt-1'>
              <Button
                onClick={() => handleOpenTriggerModal(activeService)}
                className='w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md font-semibold text-xs h-8 sm:h-9 gap-1.5'
                title={`Deploy ${activeService === 'web_server' ? 'Web Server' : 'Static Site'}`}
              >
                <Rocket className='h-3.5 w-3.5' />
                <span>Deploy</span>
              </Button>

              <Button
                onClick={() => handleOpenTriggerModal('both')}
                variant='outline'
                className='w-full border-purple-500/40 hover:bg-purple-500/10 text-purple-600 dark:text-purple-300 font-semibold text-xs h-8 sm:h-9 gap-1.5'
                title='Deploy both Web Server and Static Site at once'
              >
                <Sparkles className='h-3.5 w-3.5 text-purple-400' />
                <span>Deploy Both</span>
              </Button>
            </div>
          </Card>
        </div>

        {/* Top-Level Section Navigation: Deployments vs Logs */}
        <div className='flex items-center gap-2 p-1.5 bg-muted/60 rounded-xl border border-border/60 max-w-md'>
          <button
            onClick={() => setMainSection('deployments')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
              mainSection === 'deployments'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Rocket className='h-4 w-4 text-blue-500' />
            <span>Deployments</span>
          </button>

          <button
            onClick={() => setMainSection('logs')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
              mainSection === 'logs'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Terminal className='h-4 w-4 text-emerald-500' />
            <span>Logs & Telemetry</span>
            <span className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse' />
          </button>
        </div>

        {/* SECTION A: DEPLOYMENT MANAGEMENT */}
        {mainSection === 'deployments' && (
          <section className='space-y-4 animate-in fade-in-50 duration-200'>
            {/* Dual Service Selector Tabs & Fast Deploy Actions */}
            <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3'>
              <div className='flex items-center gap-2 p-1 bg-muted/60 rounded-xl border'>
                <button
                  onClick={() => handleServiceTabChange('web_server')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeService === 'web_server'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Server className='h-3.5 w-3.5 text-blue-500' />
                  <span>Web Server (Backend)</span>
                  {servicesConfig?.web_server?.is_configured && (
                    <span className='w-1.5 h-1.5 rounded-full bg-emerald-500' />
                  )}
                </button>

                <button
                  onClick={() => handleServiceTabChange('static_site')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeService === 'static_site'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Globe className='h-3.5 w-3.5 text-indigo-400' />
                  <span>Static Site (Frontend)</span>
                  {servicesConfig?.static_site?.is_configured && (
                    <span className='w-1.5 h-1.5 rounded-full bg-emerald-500' />
                  )}
                </button>
              </div>

              {/* Filter, Search & Quick Deploy Both Bar */}
              <div className='flex items-center gap-2 flex-wrap sm:flex-nowrap'>
                <Input
                  placeholder='Search by ID or commit...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='h-8 text-xs w-full sm:w-56'
                />
                <div className='flex items-center gap-1 bg-muted/40 p-0.5 rounded-lg border text-xs'>
                  {['all', 'live', 'building', 'failed'].map((st) => (
                    <button
                      key={st}
                      onClick={() => setStatusFilter(st)}
                      className={`px-2 py-1 rounded-md text-[11px] font-medium capitalize transition-colors ${
                        statusFilter === st
                          ? 'bg-background shadow-xs text-foreground font-semibold'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>

                <Button
                  size='sm'
                  onClick={() => handleOpenTriggerModal('both')}
                  className='h-8 px-2.5 text-xs bg-purple-600 hover:bg-purple-700 text-white gap-1.5 shadow-sm font-semibold flex-shrink-0'
                >
                  <Zap className='h-3.5 w-3.5' />
                  <span className='hidden md:inline'>Deploy Both</span>
                </Button>
              </div>
            </div>

            {/* Deploys Table / Feed - Generous spacing with icon-only action buttons */}
            <Card className={`border overflow-hidden transition-colors ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
              <div className='w-full overflow-x-auto'>
                <table className='w-full text-xs text-left border-collapse min-w-[640px] sm:min-w-full'>
                  <thead>
                    <tr className='border-b border-border/60 bg-muted/30 text-muted-foreground font-semibold'>
                      <th className='py-3 px-4 w-36'>Deploy ID</th>
                      <th className='py-3 px-3 w-28'>Status</th>
                      <th className='py-3 px-4'>Commit & Message</th>
                      <th className='py-3 px-3 w-20 hidden md:table-cell'>Trigger</th>
                      <th className='py-3 px-4 w-36 whitespace-nowrap'>Created</th>
                      <th className='py-3 px-4 w-24 text-right'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-border/40'>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className='p-8 text-center text-muted-foreground'>
                          <RefreshCw className='h-5 w-5 animate-spin mx-auto mb-2 text-blue-500' />
                          Loading deployments for {activeService === 'web_server' ? 'Web Server' : 'Static Site'}...
                        </td>
                      </tr>
                    ) : filteredDeploys.length === 0 ? (
                      <tr>
                        <td colSpan={6} className='p-8 text-center text-muted-foreground'>
                          No deployments found matching your filter.
                        </td>
                      </tr>
                    ) : (
                      filteredDeploys.map((d) => {
                        const isLive = d.status === 'live';
                        const isInProgress = d.status === 'build_in_progress' || d.status === 'queued';
                        const isCanceling = cancelingDeployId === d.id;

                        return (
                          <tr
                            key={d.id}
                            className='hover:bg-muted/20 transition-colors group'
                          >
                            {/* Deploy ID */}
                            <td className='py-3 px-4 font-mono font-medium truncate'>
                              <div className='flex items-center gap-1.5 min-w-0'>
                                <span className='text-foreground truncate' title={d.id}>{d.id}</span>
                                <button
                                  onClick={() => handleCopy(d.id, d.id)}
                                  className='opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground flex-shrink-0'
                                  title='Copy Deploy ID'
                                >
                                  {copiedId === d.id ? (
                                    <Check className='h-3 w-3 text-emerald-500' />
                                  ) : (
                                    <Copy className='h-3 w-3' />
                                  )}
                                </button>
                              </div>
                            </td>

                            {/* Status */}
                            <td className='py-3 px-3 whitespace-nowrap'>
                              {getStatusBadge(d.status)}
                            </td>

                            {/* Commit */}
                            <td className='py-3 px-4 min-w-0'>
                              <div className='flex items-start gap-1.5 min-w-0'>
                                <GitCommit className='h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5' />
                                <div className='min-w-0'>
                                  <p className='font-medium text-foreground truncate max-w-xs sm:max-w-md' title={d.commit?.message || 'Manual build'}>
                                    {d.commit?.message || 'Manual build'}
                                  </p>
                                  {d.commit?.id && (
                                    <span className='font-mono text-[10px] text-muted-foreground block truncate'>
                                      {d.commit.id.substring(0, 7)} {d.finishedAt ? `• ${getDuration(d.createdAt, d.finishedAt)}` : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Trigger */}
                            <td className='py-3 px-3 whitespace-nowrap hidden md:table-cell'>
                              <Badge variant='outline' className='text-[10px] uppercase font-mono font-semibold px-1.5 py-0'>
                                {d.trigger || 'api'}
                              </Badge>
                            </td>

                            {/* Created */}
                            <td className='py-3 px-4 whitespace-nowrap text-muted-foreground font-mono text-[11px]'>
                              {formatDate(d.createdAt)}
                            </td>

                            {/* Actions (Icon-only buttons with tooltips) */}
                            <td className='py-3 px-4 text-right whitespace-nowrap'>
                              <div className='flex items-center justify-end gap-1.5'>
                                {/* Inspect Button */}
                                <Button
                                  variant='outline'
                                  size='icon'
                                  onClick={() => handleInspectDeploy(d.id)}
                                  className='h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/80 transition-colors'
                                  title='Inspect deploy details'
                                >
                                  <Eye className='h-3.5 w-3.5' />
                                </Button>

                                {/* Cancel Button for in-progress */}
                                {isInProgress && (
                                  <Button
                                    variant='destructive'
                                    size='icon'
                                    disabled={isCanceling}
                                    onClick={() => handleCancelDeploy(d.id)}
                                    className='h-7 w-7 rounded-lg'
                                    title='Cancel in-progress deploy'
                                  >
                                    <Ban className='h-3.5 w-3.5' />
                                  </Button>
                                )}

                                {/* Rollback Button for inactive / completed */}
                                {!isLive && !isInProgress && (
                                  <Button
                                    variant='outline'
                                    size='icon'
                                    onClick={() => {
                                      setRollbackDeployTarget(d);
                                      setShowRollbackDialog(true);
                                    }}
                                    className='h-7 w-7 rounded-lg text-muted-foreground hover:text-blue-400 hover:border-blue-500/40 hover:bg-blue-500/10 transition-colors'
                                    title='Rollback to this build'
                                  >
                                    <RotateCcw className='h-3.5 w-3.5' />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        )}

        {/* SECTION B: LOGS & TELEMETRY SECTION */}
        {mainSection === 'logs' && (
          <section className='space-y-4 animate-in fade-in-50 duration-200'>
            {/* Logs Sub-tabs Bar & Global Logs Controls */}
            <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-border/60 pb-3'>
              {/* Sub-tab Pills */}
              <div className='flex items-center gap-2 p-1 bg-muted/60 rounded-xl border'>
                <button
                  onClick={() => {
                    setActiveLogTab('server');
                    fetchLogs('server');
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeLogTab === 'server'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Terminal className='h-3.5 w-3.5 text-emerald-400' />
                  <span>Server Logs (Terminal)</span>
                  <Badge variant='outline' className='text-[10px] px-1.5 py-0 font-mono'>
                    {serverLogs.length}
                  </Badge>
                </button>

                <button
                  onClick={() => {
                    setActiveLogTab('internal');
                    fetchLogs('internal');
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeLogTab === 'internal'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Code2 className='h-3.5 w-3.5 text-blue-400' />
                  <span>Internal APIs</span>
                  <Badge variant='outline' className='text-[10px] px-1.5 py-0 font-mono'>
                    {internalApiLogs.length}
                  </Badge>
                </button>

                <button
                  onClick={() => {
                    setActiveLogTab('external');
                    fetchLogs('external');
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeLogTab === 'external'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Globe className='h-3.5 w-3.5 text-purple-400' />
                  <span>External APIs</span>
                  <Badge variant='outline' className='text-[10px] px-1.5 py-0 font-mono'>
                    {externalApiLogs.length}
                  </Badge>
                </button>
              </div>

              {/* Logs Action Toolbar */}
              <div className='flex items-center gap-2 flex-wrap'>
                {/* Search Bar */}
                <div className='relative w-full sm:w-48'>
                  <Search className='h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground' />
                  <Input
                    placeholder='Filter logs...'
                    value={logSearchQuery}
                    onChange={(e) => setLogSearchQuery(e.target.value)}
                    className='h-8 text-xs pl-8'
                  />
                </div>

                {/* Sub-Filters based on active tab */}
                {activeLogTab === 'server' && (
                  <div className='flex items-center gap-1 bg-muted/40 p-0.5 rounded-lg border text-xs'>
                    {['ALL', 'INFO', 'WARNING', 'ERROR', 'DEBUG'].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setLogLevelFilter(lvl)}
                        className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                          logLevelFilter === lvl
                            ? 'bg-background shadow-xs text-foreground font-semibold'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {lvl === 'WARNING' ? 'WARN' : lvl}
                      </button>
                    ))}
                  </div>
                )}

                {activeLogTab === 'internal' && (
                  <div className='flex items-center gap-1 bg-muted/40 p-0.5 rounded-lg border text-xs'>
                    {['all', '2xx', '4xx', '5xx'].map((st) => (
                      <button
                        key={st}
                        onClick={() => setLogStatusFilter(st)}
                        className={`px-2 py-1 rounded-md text-[11px] font-medium uppercase transition-colors ${
                          logStatusFilter === st
                            ? 'bg-background shadow-xs text-foreground font-semibold'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                )}

                {activeLogTab === 'external' && (
                  <div className='flex items-center gap-1 bg-muted/40 p-0.5 rounded-lg border text-xs'>
                    {['all', 'google gemini ai', 'email'].map((srv) => (
                      <button
                        key={srv}
                        onClick={() => setLogServiceFilter(srv)}
                        className={`px-2 py-1 rounded-md text-[11px] font-medium capitalize transition-colors ${
                          logServiceFilter === srv
                            ? 'bg-background shadow-xs text-foreground font-semibold'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {srv === 'google gemini ai' ? 'Gemini AI' : srv === 'email' ? 'Email / Webhook' : 'All'}
                      </button>
                    ))}
                  </div>
                )}

                {/* External Token Summary Counter */}
                {activeLogTab === 'external' && externalTokenSummary && (
                  <div
                    className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs font-mono font-semibold'
                    title={`Total Prompt Tokens: ${externalTokenSummary.prompt_tokens?.toLocaleString() || 0} | Total Completion Tokens: ${externalTokenSummary.completion_tokens?.toLocaleString() || 0}`}
                  >
                    <Zap className='h-3.5 w-3.5 text-amber-500 flex-shrink-0' />
                    <span>{externalTokenSummary.total_tokens?.toLocaleString() || 0} Tokens</span>
                    <span className='text-[10px] opacity-75 font-sans hidden sm:inline'>({externalTokenSummary.calls_count || 0} Gemini {externalTokenSummary.calls_count === 1 ? 'call' : 'calls'})</span>
                  </div>
                )}

                {/* Live Stream Toggle Button */}
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setIsStreamingLogs((prev) => !prev)}
                  className='h-8 px-2 text-xs gap-1.5 font-medium'
                  title={isStreamingLogs ? 'Pause live stream' : 'Resume live stream'}
                >
                  {isStreamingLogs ? (
                    <>
                      <span className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse' />
                      <span className='hidden sm:inline'>Live</span>
                    </>
                  ) : (
                    <>
                      <Pause className='h-3 w-3 text-amber-500' />
                      <span className='hidden sm:inline'>Paused</span>
                    </>
                  )}
                </Button>

                {/* Auto Scroll Toggle (for Server terminal) */}
                {activeLogTab === 'server' && (
                  <Button
                    variant={autoScroll ? 'secondary' : 'outline'}
                    size='sm'
                    onClick={() => setAutoScroll((prev) => !prev)}
                    className='h-8 px-2 text-xs gap-1'
                    title={autoScroll ? 'Auto-scroll is ON' : 'Auto-scroll is OFF'}
                  >
                    <ArrowDownCircle className={`h-3.5 w-3.5 ${autoScroll ? 'text-blue-500' : 'text-muted-foreground'}`} />
                    <span className='hidden md:inline'>Scroll</span>
                  </Button>
                )}

                {/* Export Log */}
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleExportLogs('log')}
                  className='h-8 px-2 text-xs gap-1'
                  title='Download log file'
                >
                  <Download className='h-3.5 w-3.5' />
                  <span className='hidden md:inline'>Export</span>
                </Button>

                {/* Clear Logs */}
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleClearLogs}
                  disabled={clearingLogs}
                  className='h-8 px-2 text-xs gap-1 text-rose-500 hover:text-rose-600 hover:bg-rose-500/10'
                  title='Clear log buffer'
                >
                  <Trash2 className='h-3.5 w-3.5' />
                  <span className='hidden md:inline'>Clear</span>
                </Button>
              </div>
            </div>

            {/* TAB 1: SERVER TERMINAL LOGS */}
            {activeLogTab === 'server' && (
              <Card className='border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden rounded-xl'>
                {/* Terminal Header Bar */}
                <div className='px-4 py-2.5 bg-slate-900/90 border-b border-slate-800 flex items-center justify-between text-xs'>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-1.5'>
                      <span className='w-3 h-3 rounded-full bg-rose-500/80 inline-block' />
                      <span className='w-3 h-3 rounded-full bg-amber-500/80 inline-block' />
                      <span className='w-3 h-3 rounded-full bg-emerald-500/80 inline-block' />
                    </div>
                    <span className='font-mono text-slate-400 font-semibold text-[11px] ml-2'>
                      django-server.stdout — live output
                    </span>
                  </div>

                  <div className='flex items-center gap-3 text-slate-400 font-mono text-[11px]'>
                    <span>Buffer: {serverLogs.length}/1000</span>
                    <button
                      onClick={() => handleCopy(serverLogs.map(l => `[${l.timestamp}] [${l.level}] [${l.logger}]: ${l.message}`).join('\n'), 'all-server-logs')}
                      className='hover:text-slate-200 transition-colors flex items-center gap-1'
                    >
                      {copiedId === 'all-server-logs' ? (
                        <Check className='h-3 w-3 text-emerald-400' />
                      ) : (
                        <Copy className='h-3 w-3' />
                      )}
                      <span>Copy All</span>
                    </button>
                  </div>
                </div>

                {/* Terminal Content Body */}
                <div className='p-4 font-mono text-[11px] sm:text-xs leading-relaxed text-slate-300 max-h-[550px] min-h-[350px] overflow-y-auto space-y-1 select-text scroll-smooth'>
                  {serverLogs.length === 0 ? (
                    <div className='p-8 text-center text-slate-500 font-mono'>
                      <Terminal className='h-6 w-6 mx-auto mb-2 opacity-40' />
                      No server log entries recorded yet. Send requests to see live backend logs.
                    </div>
                  ) : (
                    serverLogs.map((log, index) => {
                      const isError = log.level === 'ERROR';
                      const isWarn = log.level === 'WARNING';
                      const isInfo = log.level === 'INFO';
                      const isDebug = log.level === 'DEBUG';

                      return (
                        <div
                          key={log.id || index}
                          className={`flex items-start gap-2 py-0.5 hover:bg-slate-900/60 px-1.5 rounded transition-colors ${
                            isError ? 'text-rose-300 bg-rose-950/20' : isWarn ? 'text-amber-200 bg-amber-950/20' : ''
                          }`}
                        >
                          <span className='text-slate-600 select-none w-8 text-right flex-shrink-0 text-[10px]'>
                            {serverLogs.length - index}
                          </span>
                          <span className='text-slate-500 text-[10px] whitespace-nowrap flex-shrink-0'>
                            {formatDate(log.timestamp).split(', ')[1] || log.timestamp}
                          </span>
                          <span
                            className={`px-1.5 py-0 rounded text-[10px] font-bold flex-shrink-0 ${
                              isError
                                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                : isWarn
                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                : isDebug
                                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            }`}
                          >
                            {log.level}
                          </span>
                          <span className='text-slate-400 font-semibold text-[11px] flex-shrink-0'>
                            [{log.logger}]:
                          </span>
                          <span className='break-all flex-1 whitespace-pre-wrap font-mono'>
                            {log.message}
                          </span>
                        </div>
                      );
                    })
                  )}
                  <div ref={terminalBottomRef} />
                </div>
              </Card>
            )}

            {/* TAB 2: INTERNAL API LOGS */}
            {activeLogTab === 'internal' && (
              <Card className={`border overflow-hidden transition-colors ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
                <div className='w-full overflow-x-auto'>
                  <table className='w-full text-xs text-left border-collapse min-w-[700px]'>
                    <thead>
                      <tr className='border-b border-border/60 bg-muted/30 text-muted-foreground font-semibold'>
                        <th className='py-3 px-4 w-20'>Method</th>
                        <th className='py-3 px-4'>Endpoint Path</th>
                        <th className='py-3 px-3 w-20'>Status</th>
                        <th className='py-3 px-3 w-24'>Latency</th>
                        <th className='py-3 px-4 w-36'>User / IP</th>
                        <th className='py-3 px-4 w-36 whitespace-nowrap'>Time</th>
                        <th className='py-3 px-4 w-20 text-right'>Details</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-border/40 font-mono'>
                      {internalApiLogs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className='p-8 text-center text-muted-foreground font-sans'>
                            No application API requests captured yet.
                          </td>
                        </tr>
                      ) : (
                        internalApiLogs.map((item) => (
                          <tr key={item.id} className='hover:bg-muted/20 transition-colors group'>
                            <td className='py-2.5 px-4 whitespace-nowrap'>
                              {getMethodBadge(item.method)}
                            </td>
                            <td className='py-2.5 px-4 font-sans font-medium text-foreground truncate max-w-xs sm:max-w-md'>
                              {item.path}
                            </td>
                            <td className='py-2.5 px-3 whitespace-nowrap'>
                              {getStatusCodeBadge(item.status_code)}
                            </td>
                            <td className='py-2.5 px-3 whitespace-nowrap text-muted-foreground'>
                              <span className={item.duration_ms > 500 ? 'text-amber-400 font-semibold' : ''}>
                                {item.duration_ms}ms
                              </span>
                            </td>
                            <td className='py-2.5 px-4 whitespace-nowrap text-muted-foreground text-[11px] truncate max-w-[140px]'>
                              <div>{item.user || 'Anonymous'}</div>
                              <div className='text-[10px] text-muted-foreground/70'>{item.client_ip}</div>
                            </td>
                            <td className='py-2.5 px-4 whitespace-nowrap text-muted-foreground text-[11px] font-sans'>
                              {formatDate(item.timestamp)}
                            </td>
                            <td className='py-2.5 px-4 text-right whitespace-nowrap'>
                              <Button
                                variant='outline'
                                size='icon'
                                onClick={() => {
                                  setSelectedLogDetail({ type: 'internal', data: item });
                                  setShowLogDetailModal(true);
                                }}
                                className='h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground'
                                title='Inspect Request Details'
                              >
                                <Eye className='h-3.5 w-3.5' />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* TAB 3: EXTERNAL API LOGS */}
            {activeLogTab === 'external' && (
              <Card className={`border overflow-hidden transition-colors ${activeThemeObj.styles?.cardBg || 'bg-slate-900/90 border-slate-800'}`}>
                <div className='w-full overflow-x-auto'>
                  <table className='w-full text-xs text-left border-collapse min-w-[760px]'>
                    <thead>
                      <tr className='border-b border-border/60 bg-muted/30 text-muted-foreground font-semibold'>
                        <th className='py-3 px-4 w-36'>External Service</th>
                        <th className='py-3 px-3 w-20'>Method</th>
                        <th className='py-3 px-4'>Destination URL</th>
                        <th className='py-3 px-3 w-20'>Status</th>
                        <th className='py-3 px-3 w-20'>Duration</th>
                        <th className='py-3 px-3 w-28'>Tokens</th>
                        <th className='py-3 px-4 w-32 whitespace-nowrap'>Time</th>
                        <th className='py-3 px-4 w-16 text-right'>Details</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-border/40 font-mono'>
                      {externalApiLogs.length === 0 ? (
                        <tr>
                          <td colSpan={8} className='p-8 text-center text-muted-foreground font-sans'>
                            No external 3rd-party requests recorded yet. Trigger an AI query or email dispatch to see telemetry.
                          </td>
                        </tr>
                      ) : (
                        externalApiLogs.map((item) => (
                          <tr key={item.id} className='hover:bg-muted/20 transition-colors group'>
                            <td className='py-2.5 px-4 whitespace-nowrap font-sans font-semibold'>
                              <div className='flex items-center gap-1.5'>
                                {item.service?.includes('Gemini') ? (
                                  <Sparkles className='h-3.5 w-3.5 text-amber-400 flex-shrink-0' />
                                ) : (
                                  <Globe className='h-3.5 w-3.5 text-purple-400 flex-shrink-0' />
                                )}
                                <span>{item.service}</span>
                              </div>
                            </td>
                            <td className='py-2.5 px-3 whitespace-nowrap'>
                              {getMethodBadge(item.method)}
                            </td>
                            <td className='py-2.5 px-4 text-foreground truncate max-w-xs sm:max-w-md'>
                              {item.url}
                            </td>
                            <td className='py-2.5 px-3 whitespace-nowrap'>
                              {getStatusCodeBadge(item.status_code)}
                            </td>
                            <td className='py-2.5 px-3 whitespace-nowrap text-muted-foreground'>
                              {item.duration_ms}ms
                            </td>
                            {/* Tokens Consumed Column */}
                            <td className='py-2.5 px-3 whitespace-nowrap'>
                              {item.token_usage ? (
                                <div
                                  className='flex flex-col'
                                  title={`Prompt: ${item.token_usage.prompt_tokens?.toLocaleString() || 0} tokens | Completion: ${item.token_usage.completion_tokens?.toLocaleString() || 0} tokens (${item.token_usage.source || 'API'})`}
                                >
                                  <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono font-bold text-[11px] bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 whitespace-nowrap w-fit'>
                                    <Zap className='h-3 w-3 text-amber-500' />
                                    {item.token_usage.total_tokens?.toLocaleString() || 0}
                                  </span>
                                  <span className='text-[9px] text-muted-foreground font-mono mt-0.5 truncate'>
                                    P:{item.token_usage.prompt_tokens || 0} • C:{item.token_usage.completion_tokens || 0}
                                  </span>
                                </div>
                              ) : (
                                <span className='text-muted-foreground font-mono text-[11px]'>—</span>
                              )}
                            </td>
                            <td className='py-2.5 px-4 whitespace-nowrap text-muted-foreground text-[11px] font-sans'>
                              {formatDate(item.timestamp)}
                            </td>
                            <td className='py-2.5 px-4 text-right whitespace-nowrap'>
                              <Button
                                variant='outline'
                                size='icon'
                                onClick={() => {
                                  setSelectedLogDetail({ type: 'external', data: item });
                                  setShowLogDetailModal(true);
                                }}
                                className='h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground'
                                title='Inspect Outgoing Payload'
                              >
                                <Eye className='h-3.5 w-3.5' />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </section>
        )}
      </main>

      {/* MODAL 1: Trigger Deploy Dialog */}
      <Dialog open={showTriggerModal} onOpenChange={setShowTriggerModal}>
        <DialogContent className='max-w-md p-5 sm:p-6'>
          <DialogHeader className='pb-2 border-b border-border/40'>
            <DialogTitle className='flex items-center gap-2 text-lg font-bold'>
              <Rocket className='h-5 w-5 text-blue-500' />
              Trigger New Deployment
            </DialogTitle>
            <DialogDescription className='text-xs'>
              Initiate a fresh rollout for your Render services.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-3 text-xs'>
            {/* Target Service Selection (Web Server, Static Site, or Both) */}
            <div className='space-y-1.5'>
              <label className='font-semibold text-foreground text-xs'>Target Service</label>
              <div className='grid grid-cols-3 gap-2'>
                <button
                  type='button'
                  onClick={() => setTriggerTargetService('web_server')}
                  className={`p-2.5 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all ${
                    triggerTargetService === 'web_server'
                      ? 'border-blue-500 bg-blue-500/10 shadow-xs font-semibold'
                      : 'border-border/60 hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className='flex items-center gap-1.5'>
                    <Server className='h-3.5 w-3.5 text-blue-500 flex-shrink-0' />
                    <span className='text-xs text-foreground font-semibold'>Web Server</span>
                  </div>
                  <div className='text-[10px] text-muted-foreground'>Backend API</div>
                </button>

                <button
                  type='button'
                  onClick={() => setTriggerTargetService('static_site')}
                  className={`p-2.5 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all ${
                    triggerTargetService === 'static_site'
                      ? 'border-indigo-500 bg-indigo-500/10 shadow-xs font-semibold'
                      : 'border-border/60 hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className='flex items-center gap-1.5'>
                    <Globe className='h-3.5 w-3.5 text-indigo-400 flex-shrink-0' />
                    <span className='text-xs text-foreground font-semibold'>Static Site</span>
                  </div>
                  <div className='text-[10px] text-muted-foreground'>Frontend UI</div>
                </button>

                <button
                  type='button'
                  onClick={() => setTriggerTargetService('both')}
                  className={`p-2.5 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all ${
                    triggerTargetService === 'both'
                      ? 'border-purple-500 bg-purple-500/15 shadow-xs font-semibold ring-1 ring-purple-500/40'
                      : 'border-border/60 hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className='flex items-center gap-1.5'>
                    <Sparkles className='h-3.5 w-3.5 text-purple-400 flex-shrink-0' />
                    <span className='text-xs text-foreground font-semibold'>Both</span>
                  </div>
                  <div className='text-[10px] text-purple-500 dark:text-purple-400 font-medium'>Deploy All</div>
                </button>
              </div>
            </div>

            {/* Commit Hash Input Space (Optional) */}
            <div className='space-y-1.5'>
              <label className='font-semibold text-foreground text-xs flex items-center justify-between'>
                <span className='flex items-center gap-1.5'>
                  <GitCommit className='h-3.5 w-3.5 text-blue-500' />
                  Commit Hash
                </span>
                <span className='text-[10px] text-muted-foreground font-normal'>Optional</span>
              </label>
              <Input
                placeholder='e.g. 7f8b92c (leave blank for latest HEAD)'
                value={commitInput}
                onChange={(e) => setCommitInput(e.target.value)}
                className='h-8 text-xs font-mono bg-background'
              />
              <p className='text-[11px] text-muted-foreground leading-relaxed'>
                Leave blank to deploy the latest commit from the branch automatically.
              </p>
            </div>

            {/* Clear Cache Toggle */}
            <div className='p-3 rounded-lg border border-border/60 bg-muted/20 space-y-2'>
              <div className='flex items-center justify-between'>
                <span className='font-semibold text-foreground'>Clear Build Cache</span>
                <Switch
                  checked={clearCacheOption}
                  onCheckedChange={setClearCacheOption}
                />
              </div>
              <p className='text-muted-foreground leading-relaxed text-[11px]'>
                When enabled, dependency caches will be purged before rebuilding. Recommended when updating dependencies or resolving cached build errors.
              </p>
            </div>

            <div className='text-[11px] text-muted-foreground flex items-start gap-1.5'>
              <Info className='h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5' />
              <span>
                {triggerTargetService === 'both'
                  ? 'Deploying will trigger simultaneous build jobs on Render for both Web Server and Static Site.'
                  : `Deploying will start a build job on Render for ${triggerTargetService === 'web_server' ? 'Web Server' : 'Static Site'}.`}
              </span>
            </div>
          </div>

          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setShowTriggerModal(false)}
              disabled={triggeringDeploy}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleTriggerDeploy}
              disabled={triggeringDeploy}
              className={`text-white gap-1.5 shadow-sm font-semibold ${
                triggerTargetService === 'both'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {triggerTargetService === 'both' ? (
                <Sparkles className='h-3.5 w-3.5' />
              ) : (
                <Rocket className='h-3.5 w-3.5' />
              )}
              {triggeringDeploy
                ? 'Starting Deploy...'
                : triggerTargetService === 'both'
                ? 'Deploy Both Services'
                : 'Deploy Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL 2: Retrieve Deploy Details Dialog */}
      <Dialog open={showRetrieveModal} onOpenChange={setShowRetrieveModal}>
        <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto p-5 sm:p-6'>
          <DialogHeader className='pb-2 border-b border-border/40'>
            <DialogTitle className='flex items-center justify-between text-lg font-bold'>
              <div className='flex items-center gap-2 min-w-0'>
                <Eye className='h-5 w-5 text-blue-500 flex-shrink-0' />
                <span className='truncate'>Deploy Inspection ({activeService === 'web_server' ? 'Web Server' : 'Static Site'})</span>
              </div>
              {selectedDeploy && (
                <div className='flex-shrink-0'>
                  {getStatusBadge(selectedDeploy.status)}
                </div>
              )}
            </DialogTitle>
            <DialogDescription className='font-mono text-xs text-muted-foreground'>
              {selectedDeploy?.id || 'Retrieving deploy metadata...'}
            </DialogDescription>
          </DialogHeader>

          {fetchingDetail ? (
            <div className='p-8 text-center text-muted-foreground'>
              <RefreshCw className='h-5 w-5 animate-spin mx-auto mb-2 text-blue-500' />
              Retrieving deployment parameters...
            </div>
          ) : selectedDeploy ? (
            <div className='space-y-4 py-2 text-xs'>
              {/* Key metadata grid */}
              <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
                <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                  <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Trigger Type</span>
                  <div className='font-mono font-medium'>{selectedDeploy.trigger || 'api'}</div>
                </div>
                <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                  <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Cache Policy</span>
                  <div className='font-mono font-medium'>{selectedDeploy.clearCache || 'do_not_clear'}</div>
                </div>
                <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                  <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Duration</span>
                  <div className='font-mono font-medium'>{getDuration(selectedDeploy.createdAt, selectedDeploy.finishedAt)}</div>
                </div>
              </div>

              {/* Commit info */}
              <div className='p-3 rounded-lg border bg-muted/20 space-y-1.5'>
                <span className='text-[10px] text-muted-foreground uppercase font-semibold flex items-center gap-1'>
                  <GitCommit className='h-3 w-3' /> Commit Details
                </span>
                <p className='font-medium text-foreground text-sm'>
                  {selectedDeploy.commit?.message || 'No commit message'}
                </p>
                {selectedDeploy.commit?.id && (
                  <div className='font-mono text-xs text-muted-foreground'>
                    Hash: {selectedDeploy.commit.id}
                  </div>
                )}
              </div>

              {/* Raw JSON Payload */}
              <div className='space-y-1.5'>
                <div className='flex items-center justify-between text-[11px] text-muted-foreground'>
                  <span className='font-semibold uppercase text-[10px]'>Raw Response Object</span>
                  <button
                    onClick={() => handleCopy(JSON.stringify(selectedDeploy, null, 2), 'raw-json')}
                    className='flex items-center gap-1 hover:text-foreground transition-colors'
                  >
                    {copiedId === 'raw-json' ? (
                      <>
                        <Check className='h-3 w-3 text-emerald-500' />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className='h-3 w-3' />
                        <span>Copy JSON</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className='bg-slate-950 p-3 rounded-lg font-mono text-[11px] text-emerald-400 overflow-x-auto border max-h-48 whitespace-pre-wrap break-all'>
                  {JSON.stringify(selectedDeploy, null, 2)}
                </pre>
              </div>
            </div>
          ) : (
            <div className='p-6 text-center text-muted-foreground'>Deploy metadata unavailable.</div>
          )}

          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setShowRetrieveModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL 3: Rollback Confirmation Dialog */}
      <AlertDialog open={showRollbackDialog} onOpenChange={setShowRollbackDialog}>
        <AlertDialogContent className='max-w-md'>
          <AlertDialogHeader>
            <AlertDialogTitle className='flex items-center gap-2 text-rose-500'>
              <AlertTriangle className='h-5 w-5' />
              Confirm Rollback ({activeService === 'web_server' ? 'Web Server' : 'Static Site'})
            </AlertDialogTitle>
            <AlertDialogDescription className='text-xs space-y-2'>
              <p>
                Are you sure you want to rollback the active {activeService === 'web_server' ? 'Web Server' : 'Static Site'} deployment to:
              </p>
              <div className='p-2.5 rounded-md bg-muted/40 border font-mono text-xs text-foreground space-y-1'>
                <div className='font-bold'>{rollbackDeployTarget?.id}</div>
                <div className='text-muted-foreground text-[11px] truncate'>
                  {rollbackDeployTarget?.commit?.message || 'Previous stable release'}
                </div>
              </div>
              <p className='text-muted-foreground'>
                This will trigger a release targeting this build and replace the current live version.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rollingBack}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRollback}
              disabled={rollingBack}
              className='bg-rose-600 hover:bg-rose-700 text-white'
            >
              {rollingBack ? 'Rolling back...' : 'Confirm Rollback'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* MODAL 4: Log Details Inspection Modal */}
      <Dialog open={showLogDetailModal} onOpenChange={setShowLogDetailModal}>
        <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto p-5 sm:p-6'>
          <DialogHeader className='pb-2 border-b border-border/40'>
            <DialogTitle className='flex items-center justify-between text-lg font-bold'>
              <div className='flex items-center gap-2 min-w-0'>
                <Code2 className='h-5 w-5 text-blue-500 flex-shrink-0' />
                <span className='truncate'>
                  {selectedLogDetail?.type === 'internal' ? 'Internal API Request Details' : 'External 3rd-Party Call Details'}
                </span>
              </div>
              {selectedLogDetail?.data?.status_code && (
                <div>{getStatusCodeBadge(selectedLogDetail.data.status_code)}</div>
              )}
            </DialogTitle>
            <DialogDescription className='font-mono text-xs text-muted-foreground'>
              {selectedLogDetail?.data?.id} — {selectedLogDetail?.data?.timestamp}
            </DialogDescription>
          </DialogHeader>

          {selectedLogDetail?.data && (
            <div className='space-y-4 py-2 text-xs'>
              {/* Top metadata grid */}
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-2.5'>
                <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                  <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Method</span>
                  <div className='font-mono font-bold'>{selectedLogDetail.data.method}</div>
                </div>
                <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                  <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Duration</span>
                  <div className='font-mono font-bold'>{selectedLogDetail.data.duration_ms}ms</div>
                </div>
                <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                  <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Target</span>
                  <div className='font-mono text-[11px] truncate'>
                    {selectedLogDetail.type === 'internal' ? selectedLogDetail.data.client_ip : selectedLogDetail.data.service}
                  </div>
                </div>
                {/* Tokens Telemetry Card */}
                {selectedLogDetail.data.token_usage ? (
                  <div className='p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 space-y-0.5'>
                    <span className='text-[10px] text-amber-500 uppercase font-semibold flex items-center gap-1'>
                      <Zap className='h-3 w-3' /> Tokens
                    </span>
                    <div className='font-mono font-bold text-amber-400 text-xs'>
                      {selectedLogDetail.data.token_usage.total_tokens?.toLocaleString()} tok
                    </div>
                    <div className='text-[9px] text-muted-foreground font-mono truncate'>
                      P:{selectedLogDetail.data.token_usage.prompt_tokens} • C:{selectedLogDetail.data.token_usage.completion_tokens}
                    </div>
                  </div>
                ) : (
                  <div className='p-2.5 rounded-lg border bg-muted/20 space-y-1'>
                    <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Service Type</span>
                    <div className='font-mono text-[11px] truncate text-muted-foreground'>
                      {selectedLogDetail.type === 'internal' ? 'Django View' : 'HTTP REST'}
                    </div>
                  </div>
                )}
              </div>

              {/* Endpoint or URL */}
              <div className='p-3 rounded-lg border bg-muted/20 space-y-1'>
                <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Endpoint URL</span>
                <div className='font-mono text-xs text-foreground break-all'>
                  {selectedLogDetail.type === 'internal' ? selectedLogDetail.data.path : selectedLogDetail.data.url}
                </div>
              </div>

              {/* Internal API query params or body */}
              {selectedLogDetail.type === 'internal' && (
                <>
                  {selectedLogDetail.data.query_params && Object.keys(selectedLogDetail.data.query_params).length > 0 && (
                    <div className='space-y-1'>
                      <span className='font-semibold text-muted-foreground text-[10px] uppercase'>Query Parameters</span>
                      <pre className='bg-slate-950 p-2.5 rounded-lg font-mono text-[11px] text-blue-300 border overflow-x-auto'>
                        {JSON.stringify(selectedLogDetail.data.query_params, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedLogDetail.data.body_preview && (
                    <div className='space-y-1'>
                      <span className='font-semibold text-muted-foreground text-[10px] uppercase'>Request Body Preview</span>
                      <pre className='bg-slate-950 p-2.5 rounded-lg font-mono text-[11px] text-emerald-400 border overflow-x-auto max-h-48'>
                        {typeof selectedLogDetail.data.body_preview === 'object'
                          ? JSON.stringify(selectedLogDetail.data.body_preview, null, 2)
                          : selectedLogDetail.data.body_preview}
                      </pre>
                    </div>
                  )}
                </>
              )}

              {/* External API request / response summary */}
              {selectedLogDetail.type === 'external' && (() => {
                const reqSummary = selectedLogDetail.data.request_summary;
                const resSummary = selectedLogDetail.data.response_summary;
                const tokenUsage = selectedLogDetail.data.token_usage || resSummary?.layer_3_gemini_raw_response?.token_usage;
                const isGeminiAi = Boolean(
                  selectedLogDetail.data.service?.includes('Gemini') ||
                  reqSummary?.layer_1_raw_user_input ||
                  reqSummary?.layer_2_prompt_enhancement
                );

                if (isGeminiAi) {
                  return (
                    <div className='space-y-3 pt-1'>
                      <div className='flex items-center gap-2 text-xs font-bold text-amber-500 dark:text-amber-400 border-b border-amber-500/20 pb-1.5'>
                        <Sparkles className='h-4 w-4' />
                        <span>Google Gemini AI Multi-Layer Execution Pipeline</span>
                      </div>

                      {/* Layer 1: Raw User Input */}
                      <div className='rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 space-y-2'>
                        <div className='flex items-center justify-between'>
                          <span className='font-bold text-blue-500 dark:text-blue-400 text-[11px] flex items-center gap-1.5'>
                            <span className='w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold'>1</span>
                            Layer 1: Raw User Input & Database Schema
                          </span>
                        </div>
                        {reqSummary?.layer_1_raw_user_input?.user_prompt && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>User Natural Language Request:</span>
                            <div className='p-2 rounded bg-background/80 font-mono text-xs text-foreground mt-0.5 border'>
                              {reqSummary.layer_1_raw_user_input.user_prompt}
                            </div>
                          </div>
                        )}
                        {reqSummary?.layer_1_raw_user_input?.failed_sql_query && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Attempted SQL Query:</span>
                            <div className='p-2 rounded bg-background/80 font-mono text-xs text-rose-300 mt-0.5 border border-rose-500/20'>
                              {reqSummary.layer_1_raw_user_input.failed_sql_query}
                            </div>
                          </div>
                        )}
                        {reqSummary?.layer_1_raw_user_input?.error_message && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Raw Database Error Encountered:</span>
                            <div className='p-2 rounded bg-rose-950/30 font-mono text-xs text-rose-400 mt-0.5 border border-rose-500/30'>
                              {reqSummary.layer_1_raw_user_input.error_message}
                            </div>
                          </div>
                        )}
                        {reqSummary?.layer_1_raw_user_input?.schema_metadata_provided && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Raw Schema Metadata Provided:</span>
                            <pre className='p-2 rounded bg-slate-950 font-mono text-[11px] text-slate-300 mt-0.5 border overflow-x-auto max-h-36'>
                              {JSON.stringify(reqSummary.layer_1_raw_user_input.schema_metadata_provided, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>

                      {/* Layer 2: Prompt Enhancement & Schema Injected Context */}
                      <div className='rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 space-y-2'>
                        <div className='flex items-center justify-between'>
                          <span className='font-bold text-purple-500 dark:text-purple-400 text-[11px] flex items-center gap-1.5'>
                            <span className='w-4 h-4 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px] font-bold'>2</span>
                            Layer 2: Prompt Enhancement & Context Injection
                          </span>
                          {reqSummary?.layer_2_prompt_enhancement?.selected_model && (
                            <Badge className='bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] font-mono'>
                              {reqSummary.layer_2_prompt_enhancement.selected_model}
                            </Badge>
                          )}
                        </div>
                        {reqSummary?.layer_2_prompt_enhancement?.template_instructions && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Prompt Template & Persona Instructions:</span>
                            <p className='text-xs text-muted-foreground mt-0.5 leading-relaxed bg-background/80 p-2 rounded border'>
                              {reqSummary.layer_2_prompt_enhancement.template_instructions}
                            </p>
                          </div>
                        )}
                        {reqSummary?.layer_2_prompt_enhancement?.system_persona_instructions && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>System Persona Instructions:</span>
                            <p className='text-xs text-muted-foreground mt-0.5 leading-relaxed bg-background/80 p-2 rounded border'>
                              {reqSummary.layer_2_prompt_enhancement.system_persona_instructions}
                            </p>
                          </div>
                        )}
                        {reqSummary?.layer_2_prompt_enhancement?.final_enhanced_prompt_dispatched && (
                          <div>
                            <div className='flex items-center justify-between text-[10px] text-muted-foreground uppercase font-semibold'>
                              <span>Full Enhanced Synthesized Prompt Sent to Gemini API:</span>
                              <button
                                onClick={() => handleCopy(reqSummary.layer_2_prompt_enhancement.final_enhanced_prompt_dispatched, 'full-prompt')}
                                className='hover:text-foreground flex items-center gap-1 text-[10px] font-normal'
                              >
                                {copiedId === 'full-prompt' ? <Check className='h-3 w-3 text-emerald-400' /> : <Copy className='h-3 w-3' />}
                                <span>Copy Full Prompt</span>
                              </button>
                            </div>
                            <pre className='p-2.5 rounded bg-slate-950 font-mono text-[11px] text-purple-300 mt-1 border overflow-x-auto max-h-48 whitespace-pre-wrap'>
                              {reqSummary.layer_2_prompt_enhancement.final_enhanced_prompt_dispatched}
                            </pre>
                          </div>
                        )}
                      </div>

                      {/* Layer 3: Raw Google Gemini Direct Response & Tokens Consumed */}
                      <div className='rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-3'>
                        <div className='flex items-center justify-between'>
                          <span className='font-bold text-amber-500 dark:text-amber-400 text-[11px] flex items-center gap-1.5'>
                            <span className='w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold'>3</span>
                            Layer 3: Raw Google Gemini Direct Response ({resSummary?.layer_3_gemini_raw_response?.duration_ms || selectedLogDetail.data.duration_ms}ms)
                          </span>
                        </div>

                        {/* Token Consumption Breakdown Card */}
                        {tokenUsage && (
                          <div className='p-2.5 rounded-lg border border-amber-500/30 bg-slate-900/90 space-y-2'>
                            <div className='flex items-center justify-between text-xs font-bold text-amber-400'>
                              <span className='flex items-center gap-1.5'>
                                <Zap className='h-3.5 w-3.5 text-amber-500' />
                                Tokens Consumed
                              </span>
                              <Badge className='font-mono text-[10px] bg-amber-500/20 border-amber-500/40 text-amber-400'>
                                Total: {tokenUsage.total_tokens?.toLocaleString()} tokens
                              </Badge>
                            </div>
                            <div className='grid grid-cols-3 gap-2 text-center'>
                              <div className='p-1.5 rounded bg-slate-950/80 border border-slate-800'>
                                <div className='text-[9px] text-muted-foreground uppercase font-semibold'>Prompt Tokens</div>
                                <div className='font-mono font-bold text-blue-400 text-xs mt-0.5'>{tokenUsage.prompt_tokens?.toLocaleString() || 0}</div>
                              </div>
                              <div className='p-1.5 rounded bg-slate-950/80 border border-slate-800'>
                                <div className='text-[9px] text-muted-foreground uppercase font-semibold'>Completion Tokens</div>
                                <div className='font-mono font-bold text-emerald-400 text-xs mt-0.5'>{tokenUsage.completion_tokens?.toLocaleString() || 0}</div>
                              </div>
                              <div className='p-1.5 rounded bg-slate-950/80 border border-slate-800'>
                                <div className='text-[9px] text-muted-foreground uppercase font-semibold'>Source / Cache</div>
                                <div className='font-mono font-bold text-purple-400 text-[10px] mt-0.5 capitalize'>
                                  {tokenUsage.cached_tokens ? `${tokenUsage.cached_tokens} cached` : tokenUsage.source || 'API'}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div>
                          <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Untouched Model Output (Direct from Gemini):</span>
                          <pre className='p-2.5 rounded bg-slate-950 font-mono text-[11px] text-amber-300 mt-1 border overflow-x-auto max-h-40 whitespace-pre-wrap'>
                            {resSummary?.layer_3_gemini_raw_response?.raw_text_output || JSON.stringify(resSummary, null, 2)}
                          </pre>
                        </div>
                      </div>

                      {/* Layer 4: Parsing, Filtering & Final UI Output */}
                      <div className='rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2'>
                        <div className='flex items-center justify-between'>
                          <span className='font-bold text-emerald-500 dark:text-emerald-400 text-[11px] flex items-center gap-1.5'>
                            <span className='w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold'>4</span>
                            Layer 4: Post-Processing, Sanitization & Filtered User Output
                          </span>
                        </div>
                        {resSummary?.layer_4_post_processing_and_filtering?.filtering_steps_applied && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Post-Processing & Filtering Steps Applied:</span>
                            <ul className='list-disc list-inside text-xs text-muted-foreground mt-0.5 space-y-0.5 bg-background/80 p-2 rounded border'>
                              {resSummary.layer_4_post_processing_and_filtering.filtering_steps_applied.map((step, idx) => (
                                <li key={idx} className='font-mono text-[11px]'>{step}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {resSummary?.layer_4_post_processing_and_filtering?.final_filtered_output_shown_to_user && (
                          <div>
                            <span className='text-[10px] text-muted-foreground uppercase font-semibold'>Clean Filtered Payload Presented to User:</span>
                            <pre className='p-2.5 rounded bg-slate-950 font-mono text-[11px] text-emerald-400 mt-1 border overflow-x-auto'>
                              {JSON.stringify(resSummary.layer_4_post_processing_and_filtering.final_filtered_output_shown_to_user, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>

                      {selectedLogDetail.data.error && (
                        <div className='space-y-1'>
                          <span className='font-semibold text-rose-400 text-[10px] uppercase'>Error Trace</span>
                          <pre className='bg-rose-950/40 p-2.5 rounded-lg font-mono text-[11px] text-rose-300 border border-rose-900/50 overflow-x-auto'>
                            {selectedLogDetail.data.error}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }

                // Standard fallback display for non-Gemini external APIs (e.g. Email/Webhooks)
                return (
                  <>
                    {selectedLogDetail.data.request_summary && (
                      <div className='space-y-1'>
                        <span className='font-semibold text-muted-foreground text-[10px] uppercase'>Outgoing Request Payload</span>
                        <pre className='bg-slate-950 p-2.5 rounded-lg font-mono text-[11px] text-blue-300 border overflow-x-auto max-h-40'>
                          {typeof selectedLogDetail.data.request_summary === 'object'
                            ? JSON.stringify(selectedLogDetail.data.request_summary, null, 2)
                            : selectedLogDetail.data.request_summary}
                        </pre>
                      </div>
                    )}

                    {selectedLogDetail.data.response_summary && (
                      <div className='space-y-1'>
                        <span className='font-semibold text-muted-foreground text-[10px] uppercase'>Response Payload / Summary</span>
                        <pre className='bg-slate-950 p-2.5 rounded-lg font-mono text-[11px] text-emerald-400 border overflow-x-auto max-h-40'>
                          {typeof selectedLogDetail.data.response_summary === 'object'
                            ? JSON.stringify(selectedLogDetail.data.response_summary, null, 2)
                            : selectedLogDetail.data.response_summary}
                        </pre>
                      </div>
                    )}

                    {selectedLogDetail.data.error && (
                      <div className='space-y-1'>
                        <span className='font-semibold text-rose-400 text-[10px] uppercase'>Error Trace</span>
                        <pre className='bg-rose-950/40 p-2.5 rounded-lg font-mono text-[11px] text-rose-300 border border-rose-900/50 overflow-x-auto'>
                          {selectedLogDetail.data.error}
                        </pre>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button variant='outline' size='sm' onClick={() => setShowLogDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
